import { Job } from '../../api/jobs';
import { Candidate, Reference } from '../../api/candidates';
import { SearchableItem, SearchResult } from './types';

// Helper function to get all searchable items from the actual API data
export const createSearchableItems = (
  jobs: Job[],
  candidates: Candidate[],
  references: Reference[]
): SearchableItem[] => {
  const jobItems = jobs.map(jobToSearchable);
  const candidateItems = candidates.map(candidateToSearchable);
  const referenceItems = references.map(ref => referenceToSearchable(ref, candidates));
  
  return [...jobItems, ...candidateItems, ...referenceItems];
};

// Mock data cache for search (will be replaced by actual API data)
let searchableItemsCache: SearchableItem[] = [];

// Function to update the cache with fresh data
export const updateSearchCache = (
  jobs: Job[],
  candidates: Candidate[],
  references: Reference[]
): void => {
  searchableItemsCache = createSearchableItems(jobs, candidates, references);
};

// Job to searchable item conversion
const jobToSearchable = (job: Job): SearchableItem => {
  // Extract important keywords from job description without making the search text too noisy
  const descriptionKeywords = job.description 
    ? job.description
        .split(/\s+/)
        .filter(word => word.length > 3 && !['with', 'that', 'this', 'have', 'from', 'your', 'will', 'about', 'should'].includes(word.toLowerCase()))
        .slice(0, 20)
        .join(' ')
    : '';
    
  return {
    id: job.id,
    searchableText: `${job.job_title} ${job.department} ${descriptionKeywords}`.toLowerCase(),
    toSearchResult: (): SearchResult => ({
      id: job.id,
      type: 'job',
      title: job.job_title,
      subtitle: `${job.department}`,
      url: `/jobs?jobId=${job.id}`
    })
  };
};

// Candidate to searchable item conversion
const candidateToSearchable = (candidate: Candidate): SearchableItem => ({
  id: candidate.id,
  searchableText: `${candidate.full_name} ${candidate.email} ${candidate.linkedin || ''} ${candidate.phone || ''} ${candidate.position || ''}`.toLowerCase(),
  toSearchResult: (): SearchResult => ({
    id: candidate.id,
    type: 'candidate',
    title: candidate.full_name,
    subtitle: candidate.email,
    url: `/candidates?id=${candidate.id}${candidate.jobId ? `&jobId=${candidate.jobId}` : ''}`
  })
});

// Reference to searchable item conversion
const referenceToSearchable = (reference: Reference, candidates: Candidate[]): SearchableItem => {
  // Find the candidate this reference belongs to
  const parentCandidate = candidates.find(c => c.references?.some(r => r.id === reference.id));
  
  return {
    id: reference.id,
    searchableText: `${reference.full_name} ${reference.email || ''} ${reference.company} ${reference.relationship_type || ''} ${reference.position || ''} ${parentCandidate?.full_name || ''}`.toLowerCase(),
    toSearchResult: (): SearchResult => ({
      id: reference.id,
      type: 'reference',
      title: reference.full_name,
      subtitle: `${reference.relationship_type} at ${reference.company}`,
      url: `/candidates?reference=${reference.id}${parentCandidate?.jobId ? `&jobId=${parentCandidate.jobId}` : ''}`,
      parentId: parentCandidate?.id
    })
  };
};

// Function to get all searchable items (either from cache or generate mock data)
export const getAllSearchableItems = (): SearchableItem[] => {
  // Return the cache if it has items
  if (searchableItemsCache.length > 0) {
    return searchableItemsCache;
  }
  
  // Fallback to empty array if no cache
  return [];
};